<template>
  <!-- MODAL -->
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-4xl sm:p-6">
              <!-- Header com título e botão de fechar -->
              <div class="flex justify-between items-start mb-6">
                <h2 class="text-xl font-bold text-gray-900">{{ title }}</h2>
                <button @click="closeDialog" type="button" class="text-gray-400 hover:text-gray-600 -mt-4">
                  <span class="sr-only">Fechar</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Formulário de Filtros -->
              <div class="mt-4">
                <form class="flex flex-wrap gap-4" @submit="sendData" method="POST">

                  <!-- UF Filter -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4">
                    <label for="uf" class="block text-sm font-medium text-gray-900">UF</label>
                    <select 
                      name="uf" 
                      v-model="selectedFilters.uf"
                      multiple
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                      <option v-for="uf in ufs" :key="uf.sigla" :value="uf.sigla">{{ uf.nome }}</option>
                    </select>
                  </div>

                  <!-- Cidade Filter -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4">
                    <label for="uf" class="block text-sm font-medium text-gray-900">Cidade</label>
                    <select 
                      name="uf" 
                      v-model="selectedFilters.cidades"
                      multiple
                      :disabled="!selectedFilters.uf"
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                      <option v-for="cidade in cidades" :key="cidade" :value="cidade.nome">{{ cidade.nome }}</option>
                      <option value="">Não se Aplica</option>
                    </select>
                  </div>
                  
                  <!-- Empresa Filter -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4">
                    <label for="empresa" class="block text-sm font-medium text-gray-900">Empresa</label>
                    <select 
                      name="empresa" 
                      multiple
                      v-model="selectedFilters.empresa"
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                      <option v-for="empresa in empresas" :key="empresa.id" :value="empresa.id">{{ empresa.nome }}</option>
                      <option value="">Não se Aplica</option>
                    </select>
                  </div>

                  <!-- Status Comercial Filter -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4">
                    <label for="status_comercial" class="block text-sm font-medium text-gray-900">Status Comercial</label>
                    <select
                      id="status_comercial"
                      name="status_comercial"
                      v-model="selectedFilters.status_comercial"
                      multiple
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    >
                      <option value="">Não se Aplica</option>
                      <option value="Cancelamento recente">Cancelamento recente</option>
                      <option value="Capital máximo">Capital máximo</option>
                      <option value="Contratou recentemente">Contratou recentemente</option>
                      <option value="Enriquecido via lemit">Enriquecido via lemit</option>
                      <option value="Falecido">Falecido</option>
                      <option value="Inadimplente">Inadimplente</option>
                      <option value="Não é mais associado">Não é mais associado</option>
                      <option value="Não quer mais contato">Não quer mais contato</option>
                      <option value="Sem margem para desconto">Sem margem para desconto</option>
                    </select>
                  </div>

                  <!-- Filtro de Idade Mínima -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/4 mb-4">
                    <label for="idade_min" class="block text-sm font-medium text-gray-900">Idade Mínima</label>
                    <input
                      v-model="selectedFilters.idade_min"
                      id="idade_min"
                      name="idade_min"
                      type="number"
                      min="0"
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                  </div>

                  <!-- Filtro de Idade Máxima -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/4 mb-4">
                    <label for="idade_max" class="block text-sm font-medium text-gray-900">Idade Máxima</label>
                    <input
                      v-model="selectedFilters.idade_max"
                      id="idade_max"
                      name="idade_max"
                      type="number"
                      min="0"
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                  </div>

                  <!-- Filtro de Carreira -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/4 mb-4">
                    <label for="idade_max" class="block text-sm font-medium text-gray-900">Carreira</label>
                    <input
                      v-model="selectedFilters.carreira"
                      id="carreira"
                      name="carreira"
                      type="text"
                      min="0"
                      class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                  </div>

                  <!-- Checkbox de Dependentes -->
                  <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4 flex items-center">
                    <input
                      id="dependente"
                      type="checkbox"
                      v-model="selectedFilters.dependentes"
                      class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label for="dependente" class="ml-2 block text-sm font-medium text-gray-900">
                      Incluir Dependentes
                    </label>
                  </div>

                  <!-- Botão de aplicar filtros -->
                  <div class="w-full mt-6">
                    <button type="submit" class="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500">Aplicar Filtros</button>
                  </div>

                  <div v-if="count !== null" class="w-full mt-4 text-center text-sm font-medium text-gray-700">
                    {{ count }} segurados encontrados com os filtros aplicados.
                  </div>
                </form>
                <!-- Novo Formulário para Agendamento (Aparece apenas se count > 0) -->
                <div v-if="count > 0" class="mt-8">
                  <form @submit.prevent="createAgendamento">
                    <!-- Campo de Nome -->
                    <div class="mb-4">
                      <label for="nome" class="block text-sm font-medium text-gray-900">Nome da Rota</label>
                      <input
                        v-model="selectedFilters.nome"
                        id="nome"
                        name="nome"
                        type="text"
                        class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      />
                    </div>
                    <div class="flex flex-wrap gap-4 w-full">
                      <!-- Checkbox de Agendores -->
                      <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4 flex items-center">
                        <input
                          id="agendandor"
                          type="checkbox"
                          v-model="selectAgendadores"
                          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label for="agendandor" class="ml-2 block text-sm font-medium text-gray-900">
                          Listar Agendadores
                        </label>
                      </div>
                      <!-- Checkbox de Consultores -->
                      <div class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2 mb-4 flex items-center">
                        <input
                          id="consultor"
                          type="checkbox"
                          v-model="selectConsultores"
                          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label for="consultor" class="ml-2 block text-sm font-medium text-gray-900">
                          Listar Consultores
                        </label>
                      </div>
                    </div>
                    <!-- Campo de Consultores -->
                    <div class="mb-4" v-if="selectConsultores">
                      <label for="consultores" class="block text-sm font-medium text-gray-900">Consutores</label>
                      <select 
                        multiple
                        v-model="selectedFilters.agendadores" 
                        id="consultores" 
                        name="consultores" 
                        class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      >
                        <option v-for="consultor in consultores" :key="consultor.email" :value="consultor.email">
                          {{ consultor.name }}
                        </option>
                      </select>
                    </div>


                    <!-- Campo de Agendadores -->
                    <div class="mb-4" v-if="selectAgendadores">
                      <label for="agendadores" class="block text-sm font-medium text-gray-900">Agendadores</label>
                      <select 
                        multiple
                        v-model="selectedFilters.agendadores" 
                        id="agendadores" 
                        name="agendadores" 
                        class="mt-2 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                      >
                        <option v-for="agendador in agendadores" :key="agendador.email" :value="agendador.email">
                          {{ agendador.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Botão de Criação do Agendamento -->
                    <div class="w-full mt-6">
                      <button type="submit" class="flex w-full justify-center rounded-md bg-green-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-green-500">
                        Criar Rota
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>

import IbgeRepository from '@/services/IbgeRepository.vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { handleUnauthorized } from '@/services/AuthSer/authServiceFunction';
  
export default {
  name: 'AgendamentosForm',
  async created(){
    this.getUfs();
  },
  props: {
    title: {
      type: String,
      required: true
    },
    count: {
      type: Number,
      required: false
    },
    open: {
      type: Boolean,
      required: true
    },
    empresas: {
      type: Array,
      required: true,
      default: () => [],
    },
    agendadores: {
      type: Array,
      required: true,
      default: () => [],
    },
    consultores: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      selectedFilters: {
        uf: [], // Agora sempre array
        empresa: [], 
        situacao: [],
        cidades: [],
        status_comercial: [],
        idade_min: null,
        idade_max: null,
        agendadores:[],
        dependentes: false,
        carreira: '',
      },
      selectAgendadores:false,
      selectConsultores:false,
      ufs: [],
      cidades:[],
      ibgeUfsPage:'https://servicodados.ibge.gov.br/api/v1/localidades/estados'
    };
  },
  methods: {
    sendData(e) {
      e.preventDefault();
      console.log(this.selectedFilters)
      this.$emit('apply-filters', this.selectedFilters);
    },
    createAgendamento() {
      const agendamentoData = {
        ...this.selectedFilters,
        nome: this.selectedFilters.nome,
        agendadores: this.selectedFilters.agendadores
      };

      this.$emit('criar-agendamento', agendamentoData);
      //this.closeDialog();
    },
    async getUfs() {
      const ibgeRepo = new IbgeRepository();
      try {
        const response = await ibgeRepo.getGeoData(this.ibgeUfsPage);
        if (response?.status === 200) {
          // Ordenar o array de UFs em ordem alfabética
          this.ufs = response.data.sort((a, b) => a.nome.localeCompare(b.nome));
        } else {
          this.error = response?.message;
        }
      } catch (error) {
        handleUnauthorized(error);
      }	
    },
    async buscarCidadesMultiplosEstados(estadosSelecionados) {
      const ibgeRepo = new IbgeRepository();

      try {
        const requests = estadosSelecionados.map(uf =>
          ibgeRepo.getGeoData(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
        );
        const responses = await Promise.all(requests);
        this.cidades = responses
        .flatMap(res => res?.data || [])
        .sort((a, b) => a.nome.localeCompare(b.nome));

      } catch (error) {
        console.error('Erro ao buscar cidades:', error);
      }
    },
    closeDialog() {
      this.selectedFilters = {
        uf: [],
        empresa: this.selectedFilters.empresa || [],
        situacao: this.selectedFilters.situacao || [],
        agendadores: this.selectedFilters.agendadores || [],
        cidades: this.selectedFilters.cidades || [],
        idade_min: this.selectedFilters.idade_min || null,
        idade_max: this.selectedFilters.idade_max || null,
        carreira: this.selectedFilters.carreira || null,
      };
      this.$emit('update-open', false);
    }
  },
  components: {
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot
  },
  watch: {
  'selectedFilters.uf': function (novosEstados) {
    if (Array.isArray(novosEstados) && novosEstados.length) {
      this.buscarCidadesMultiplosEstados(novosEstados);
    } else {
      this.cidades = []; // limpa cidades se nada selecionado
    }
  },
},
}
</script>
  